import React, { createContext, useContext, useEffect, useState } from 'react'
import { useUserTypeSimple } from '../hooks/useUserTypeSimple'
import { useAuth } from '../contexts/AuthContext'

interface Theme {
  primary: string
  secondary: string
  accent: string
  background: string
  text: string
  mode: 'light' | 'dark'
  sidebar?: {
    bg: string
    text: string
    hover: string
  }
  header?: {
    bg: string
    text: string
  }
}

interface ThemeContextType {
  theme: Theme
  branding: {
    name: string
    shortName?: string
    logo?: string
    tagline?: string
  }
  isLoading: boolean
}

const defaultTheme: Theme = {
  primary: '#9CA3AF',
  secondary: '#000000',
  accent: '#FFFFFF',
  background: '#0F0F0F',
  text: '#FFFFFF',
  mode: 'dark',
  sidebar: {
    bg: '#1a1a1a',
    text: '#9CA3AF',
    hover: '#FFFFFF'
  },
  header: {
    bg: '#000000',
    text: '#9CA3AF'
  }
}

const defaultBranding = {
  name: 'Facility Manager',
  shortName: 'FM',
  logo: '/logo.png'
}

const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  branding: defaultBranding,
  isLoading: false
})

export function useTheme() {
  const context = useContext(ThemeContext)
  // Defensive: Return default context instead of throwing during initialization
  if (!context) {
    console.warn('useTheme called outside ThemeProvider, returning defaults')
    return {
      theme: defaultTheme,
      branding: defaultBranding,
      isLoading: false
    }
  }
  return context
}

export function ThemeProviderSimple({ children }: { children: React.ReactNode }) {
  // Defensive: Initialize with safe defaults
  const [theme, setTheme] = useState<Theme>(defaultTheme)
  const [branding, setBranding] = useState(defaultBranding)

  // Hooks must be called unconditionally at the top level (React Rules of Hooks)
  const userTypeData = useUserTypeSimple()
  useAuth() // Call to ensure context is available

  // Defensive: Safely extract values with null checks
  const userTheme = userTypeData?.theme ?? null
  const userBranding = userTypeData?.branding ?? null
  const userType = userTypeData?.userType ?? null

  useEffect(() => {
    try {
      const themeToApply = userTheme || defaultTheme
      const brandingToApply = userBranding || defaultBranding

      console.log('🎨 Applying user theme:', userType, brandingToApply?.name)
      setTheme(themeToApply)
      setBranding(brandingToApply)
      applyThemeToDOM(themeToApply)

      // Update page title safely
      if (document?.title !== undefined) {
        document.title = brandingToApply?.name || 'Facility Manager'
      }
    } catch (_error) {
      console.error('Theme application failed:', _error)
      // Fallback to default theme on error
      setTheme(defaultTheme)
      setBranding(defaultBranding)
      applyThemeToDOM(defaultTheme)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTheme, userBranding, userType])


  const applyThemeToDOM = (theme: Theme) => {
    // Defensive: Ensure document and theme exist
    if (!document?.documentElement || !theme) {
      console.warn('Cannot apply theme: document or theme not available')
      return
    }

    try {
      const root = document.documentElement

      console.log('🎨 Applying theme to DOM:', {
        primary: theme.primary,
        background: theme.background,
        text: theme.text,
        mode: theme.mode
      })
    
    // Primary colors (match theme.css variable names)
    // NOTE: --primary-color and --accent-color are set by BrandingContext from branding_settings
    // DO NOT set them here to avoid overwriting the correct org colors
    // root.style.setProperty('--primary-color', theme.primary) // Disabled - let BrandingContext handle this
    root.style.setProperty('--background-color', theme.background)
    // root.style.setProperty('--accent-color', theme.accent) // Disabled - let BrandingContext handle this
    root.style.setProperty('--text-color', theme.text)
    
    // Also set the new naming convention for compatibility
    root.style.setProperty('--color-primary', theme.primary)
    root.style.setProperty('--color-secondary', theme.secondary)
    root.style.setProperty('--color-accent', theme.accent)
    root.style.setProperty('--color-background', theme.background)
    root.style.setProperty('--color-text', theme.text)
    
    // Component colors
    root.style.setProperty('--sidebar-bg', theme.sidebar?.bg || theme.secondary)
    root.style.setProperty('--sidebar-text', theme.sidebar?.text || theme.text)
    root.style.setProperty('--sidebar-hover', theme.sidebar?.hover || theme.primary)
    
    root.style.setProperty('--header-bg', theme.header?.bg || theme.primary)
    root.style.setProperty('--header-text', theme.header?.text || '#FFFFFF')
    
    // Mode
    root.style.setProperty('--theme-mode', theme.mode)
    root.setAttribute('data-theme', theme.mode)
    
    // Derived colors (match theme.css expectations)
    // NOTE: --primary-rgb is set by BrandingContext from branding_settings table
    // DO NOT set it here to avoid overwriting the correct org color
    // root.style.setProperty('--primary-rgb', hexToRgb(theme.primary)) // Disabled - let BrandingContext handle this
    root.style.setProperty('--color-primary-rgb', hexToRgb(theme.primary))
    root.style.setProperty('--color-secondary-rgb', hexToRgb(theme.secondary))
    
    // Generate hover and variant colors
    const isLight = theme.mode === 'light'
    root.style.setProperty('--primary-hover', isLight ? darkenColor(theme.primary, 0.1) : lightenColor(theme.primary, 0.1))
    root.style.setProperty('--primary-light', lightenColor(theme.primary, 0.2))
    root.style.setProperty('--primary-dark', darkenColor(theme.primary, 0.2))
    
    // Background variants
    if (isLight) {
      // For light themes, use white for content areas and very light grays for contrast
      root.style.setProperty('--background-light', '#FFFFFF') // Pure white for main content
      root.style.setProperty('--background-dark', '#EDF2F7') // Very light gray for contrast areas
    } else {
      // For dark themes, use the original logic
      root.style.setProperty('--background-light', lightenColor(theme.background, 0.1))
      root.style.setProperty('--background-dark', darkenColor(theme.background, 0.1))
    }
    
    // Set text and border colors optimized for theme mode
    if (isLight) {
      root.style.setProperty('--text-secondary', `rgba(45, 55, 72, 0.7)`)
      root.style.setProperty('--text-muted', `rgba(45, 55, 72, 0.5)`)
      root.style.setProperty('--border-color', `rgba(227, 6, 19, 0.15)`) // Use primary for borders
      root.style.setProperty('--border-light', `rgba(227, 6, 19, 0.1)`)
    } else {
      root.style.setProperty('--text-secondary', `rgba(255, 255, 255, 0.7)`)
      root.style.setProperty('--text-muted', `rgba(255, 255, 255, 0.5)`)
      root.style.setProperty('--border-color', `rgba(${hexToRgb(theme.primary)}, 0.2)`)
      root.style.setProperty('--border-light', `rgba(${hexToRgb(theme.primary)}, 0.3)`)
    }
    
      // We're sticking with dark theme as the base, no need for theme classes
      document.body.classList.remove('light-theme')
      document.body.classList.remove('dark-theme')

      // Apply to body
      document.body.style.backgroundColor = theme.background
      document.body.style.color = theme.text
    } catch (error) {
      console.error('Failed to apply theme to DOM:', error)
      // Silently fail - app will use browser defaults
    }
  }

  const hexToRgb = (hex: string): string => {
    // Defensive: Handle invalid hex values
    if (!hex || typeof hex !== 'string') {
      return '0, 0, 0'
    }
    try {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '0, 0, 0'
    } catch (_error) {
      console.warn('Invalid hex color:', hex)
      return '0, 0, 0'
    }
  }

  const lightenColor = (hex: string, percent: number): string => {
    // Defensive: Handle invalid inputs
    if (!hex || typeof hex !== 'string') {
      return '#FFFFFF'
    }
    try {
      const num = parseInt(hex.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent * 100)
      const R = (num >> 16) + amt
      const G = (num >> 8 & 0x00FF) + amt
      const B = (num & 0x0000FF) + amt
      return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)
    } catch (_error) {
      console.warn('Invalid hex color for lighten:', hex)
      return '#FFFFFF'
    }
  }

  const darkenColor = (hex: string, percent: number): string => {
    // Defensive: Handle invalid inputs
    if (!hex || typeof hex !== 'string') {
      return '#000000'
    }
    try {
      const num = parseInt(hex.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent * 100)
      const R = (num >> 16) - amt
      const G = (num >> 8 & 0x00FF) - amt
      const B = (num & 0x0000FF) - amt
      return '#' + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
        (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
        (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1)
    } catch (_error) {
      console.warn('Invalid hex color for darken:', hex)
      return '#000000'
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, branding, isLoading: false }}>
      {children}
    </ThemeContext.Provider>
  )
}